import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'

import { addDays, dayFormat, dayOf, resolveLocale, utcNoon, type CalendarDay, type CalendarFormat } from './calendar'
import { cn, focusRing, targetSize } from './cn'
import { markEdited } from './edited'
import { FieldError } from './FieldError'
import { FieldLabelContext } from './field-label'
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, X } from './icons'

// The picker's days, months and years are Gregorian, so every label is too, in any locale.
const gregorian: Intl.DateTimeFormatOptions = { calendar: 'gregory' }

const popoverWidth = 320
const viewportGutter = 16

type View = 'day' | 'month' | 'year'

export interface DatePickerProps extends CalendarFormat {
  /** The picked day, `YYYY-MM-DD`, or empty for none. */
  readonly value: CalendarDay | ''
  /** Called with the picked day, or `null` when it is cleared. */
  readonly onChange: (day: CalendarDay | null) => void
  /** A caption above the field. Left out inside a `FormRow`, whose label then names the field. */
  readonly label?: string
  /** Follows the row's label when a `FormRow` holds several fields ("Starts" → "Starts After"). */
  readonly labelSuffix?: string
  /** Shown while no day is picked; it also names the field when there is no label and no row. */
  readonly placeholder: string
  /** The id of a hint that describes the field, read before any error. */
  readonly describedBy?: string | undefined
  /**
   * The error message; when set, the stroke turns error and the field names the message. The field
   * is marked `data-invalid`, as a button may not carry `aria-invalid`, so a failed save focuses it.
   */
  readonly error?: string | undefined
  /** Adds a button that clears the day once one is picked. */
  readonly clearable?: boolean
  /** Days marked with a dot, such as days with events. */
  readonly markedDays?: readonly CalendarDay[]
  /** Highlighted as today, and the month shown when nothing is picked. Now, by default. */
  readonly today?: Date
  readonly labels: {
    /** The popover's heading, such as "Select Date". */
    readonly title: string
    readonly previous: string
    readonly next: string
    readonly cancel: string
    readonly ok: string
    /** The clear button's accessible name, such as "Clear From". */
    readonly clear: string
    /** What a marked day's dot means, such as "Has events"; read with each marked day. */
    readonly marked?: string
  }
  /** Sizes the field; `w-44` by default. */
  readonly className?: string
}

/**
 * The mockups' date picker (`components/ui/DatePicker.jsx`): a field that opens a "Select Date"
 * popover with day, month and year views. A day picks at once; a month or year waits for Ok. The
 * popover is a dialog that closes on Escape, on tabbing out, on the page scrolling or on a click
 * outside, and gives focus back to the field. It is placed fixed from the field, so a scrolling
 * panel does not clip it.
 *
 * The days are a grid, as WAI-ARIA's date picker dialog pattern has it: one Tab stop, on the
 * picked day or today as the picker opens. Arrows move by a day or a week, Home and End to the
 * week's first and last day, Page Up and Page Down by a month, with Shift by a year, and Enter or
 * Space picks. Keys that would scroll the page do not, so they never close it.
 */
export function DatePicker({
  value,
  onChange,
  label,
  labelSuffix,
  placeholder,
  describedBy,
  error,
  clearable = false,
  markedDays,
  today,
  labels,
  className = 'w-44',
  locale: requestedLocale,
  timeZone,
}: DatePickerProps) {
  const locale = resolveLocale(requestedLocale)
  // Read on every render, so a picker left open past midnight moves to the new day.
  const todayDay = dayOf(today ?? new Date(), timeZone)
  const selected = value === '' ? null : value
  const base = selected ?? todayDay

  const [open, setOpen] = useState(false)
  const [view, setView] = useState<View>('day')
  // The month shown in the day view, as its first day.
  const [cursor, setCursor] = useState(() => `${base.slice(0, 7)}-01`)
  const [pendingMonth, setPendingMonth] = useState(() => Number(base.slice(5, 7)) - 1)
  const [pendingYear, setPendingYear] = useState(() => Number(base.slice(0, 4)))
  const [yearStart, setYearStart] = useState(() => Number(base.slice(0, 4)) - 9)
  // The day the grid's one Tab stop is on, and whether focus moves to it once it is shown.
  const [focusDay, setFocusDay] = useState<CalendarDay>(base)
  const focusDayNext = useRef(false)
  const [position, setPosition] = useState<CSSProperties>({})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const headingId = useId()
  const shownId = useId()
  const suffixId = useId()
  const errorId = useId()
  const rowLabelId = useContext(FieldLabelContext)

  const formats = useMemo(
    () => ({
      field: dayFormat(locale, { ...gregorian, dateStyle: 'medium' }),
      headline: dayFormat(locale, { ...gregorian, weekday: 'short', month: 'short', day: 'numeric' }),
      monthYear: dayFormat(locale, { ...gregorian, month: 'short', year: 'numeric' }),
      shortMonth: dayFormat(locale, { ...gregorian, month: 'short' }),
      longMonth: dayFormat(locale, { ...gregorian, month: 'long' }),
      fullDay: dayFormat(locale, { ...gregorian, weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }),
      weekday: dayFormat(locale, { ...gregorian, weekday: 'narrow' }),
      longWeekday: dayFormat(locale, { ...gregorian, weekday: 'long' }),
    }),
    [locale],
  )

  const close = useCallback(() => {
    setOpen(false)
    triggerRef.current?.focus()
  }, [])

  useEffect(() => {
    if (open) {
      dialogRef.current?.focus()
    }
  }, [open])

  // After every render, so focus follows the grid's Tab stop once it is shown: as the picker opens,
  // as the keyboard moves it and as the day view comes back.
  useEffect(() => {
    if (open && focusDayNext.current) {
      focusDayNext.current = false
      dialogRef.current?.querySelector<HTMLElement>('[data-day][tabindex="0"]')?.focus()
    }
  })

  useEffect(() => {
    const dialog = dialogRef.current
    if (!open || dialog === null) {
      return undefined
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && dialog?.contains(document.activeElement) === true) {
        event.stopPropagation()
        close()
      }
    }
    // Only a real move elsewhere (Tab) closes it; a click or a view change has no related target.
    function onFocusOut(event: FocusEvent) {
      const target = event.relatedTarget
      if (target instanceof Node && dialog?.contains(target) === false) {
        setOpen(false)
      }
    }
    function onMove(event: Event) {
      // The popover's own scroll, on a short viewport, is not the page's.
      if (event.type === 'scroll' && event.target instanceof Node && dialogRef.current?.contains(event.target) === true) {
        return
      }
      close()
    }
    document.addEventListener('keydown', onKeyDown)
    dialog.addEventListener('focusout', onFocusOut)
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      dialog.removeEventListener('focusout', onFocusOut)
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, close])

  const openPicker = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (rect !== undefined) {
      const width = Math.min(popoverWidth, window.innerWidth - 2 * viewportGutter)
      const alignRight = rect.left + width > window.innerWidth - viewportGutter
      const left = Math.min(
        Math.max(alignRight ? rect.right - width : rect.left, viewportGutter),
        window.innerWidth - viewportGutter - width,
      )
      const above = Math.max(rect.top - 8 - viewportGutter, 0)
      const below = Math.max(window.innerHeight - rect.bottom - 8 - viewportGutter, 0)
      setPosition(
        above > below
          ? { bottom: window.innerHeight - rect.top + 8, left, width, maxHeight: above }
          : { top: rect.bottom + 8, left, width, maxHeight: below },
      )
    }
    // Today read now, at the click: no render marks midnight passing on an idle page.
    const start = selected ?? dayOf(today ?? new Date(), timeZone)
    setCursor(`${start.slice(0, 7)}-01`)
    setPendingMonth(Number(start.slice(5, 7)) - 1)
    setPendingYear(Number(start.slice(0, 4)))
    setYearStart(Number(start.slice(0, 4)) - 9)
    setFocusDay(start)
    focusDayNext.current = true
    setView('day')
    setOpen(true)
  }, [selected, today, timeZone])

  const pickDay = useCallback(
    (day: CalendarDay) => {
      markEdited(triggerRef.current)
      onChange(day)
      close()
    },
    [onChange, close],
  )

  const clear = useCallback(() => {
    markEdited(triggerRef.current)
    onChange(null)
    triggerRef.current?.focus()
  }, [onChange])

  const cursorYear = Number(cursor.slice(0, 4))
  const cursorMonth = Number(cursor.slice(5, 7)) - 1

  const drillUp = useCallback(() => {
    if (view === 'day') {
      setPendingMonth(cursorMonth)
      setPendingYear(cursorYear)
      setYearStart(cursorYear - 9)
      setView('month')
    } else if (view === 'month') {
      setYearStart(pendingYear - 9)
      setView('year')
    } else {
      setView('month')
    }
  }, [view, cursorMonth, cursorYear, pendingYear])

  const step = useCallback(
    (direction: 1 | -1) => {
      if (view === 'day') {
        setCursor(monthOf(cursorYear, cursorMonth + direction))
      } else if (view === 'month') {
        setPendingYear((year) => year + direction)
      } else {
        setYearStart((year) => year + 12 * direction)
      }
    },
    [view, cursorYear, cursorMonth],
  )
  const previous = useCallback(() => step(-1), [step])
  const next = useCallback(() => step(1), [step])

  // The footer's buttons go with their view, so focus moves to the dialog first, not the page.
  const switchTo = useCallback((to: View) => {
    dialogRef.current?.focus()
    setView(to)
  }, [])
  const confirmMonth = useCallback(() => {
    setCursor(monthOf(pendingYear, pendingMonth))
    focusDayNext.current = true
    switchTo('day')
  }, [pendingYear, pendingMonth, switchTo])
  const cancelMonth = useCallback(() => {
    setPendingMonth(cursorMonth)
    setPendingYear(cursorYear)
    focusDayNext.current = true
    switchTo('day')
  }, [cursorMonth, cursorYear, switchTo])

  const moveFocus = useCallback((day: CalendarDay) => {
    focusDayNext.current = true
    setFocusDay(day)
    setCursor(monthOf(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1))
  }, [])

  // Keys that scroll the page would close the picker, which closes as the page scrolls.
  const holdScrollKeys = useCallback((event: ReactKeyboardEvent<HTMLDialogElement>) => {
    if (scrollKeys.has(event.key) || (event.key === ' ' && event.target === event.currentTarget)) {
      event.preventDefault()
    }
  }, [])
  const confirmYear = useCallback(() => switchTo('month'), [switchTo])
  const cancelYear = useCallback(() => {
    setPendingYear(cursorYear)
    switchTo('month')
  }, [cursorYear, switchTo])

  const hasLabel = label !== undefined && label !== ''
  const fieldName = hasLabel ? label : placeholder
  const formatted = selected === null ? null : formats.field.format(utcNoon(selected))
  const shown = formatted ?? placeholder
  // The name holds the text the field shows, as WCAG 2.5.3 asks, so "Starts: No date", not "Starts".
  const accessibleName = shown === fieldName ? shown : `${fieldName}: ${shown}`
  // In a FormRow the row's label names it, with its "required", then the text the field shows.
  const inRow = !hasLabel && rowLabelId !== null
  const suffixed = inRow && labelSuffix !== undefined && labelSuffix !== ''
  const labelledBy = inRow ? [rowLabelId, suffixed ? suffixId : null, shownId].filter(Boolean).join(' ') : undefined
  const descriptions = [describedBy, error === undefined ? undefined : errorId].filter(Boolean).join(' ')
  const headline = view === 'day' ? (selected ?? todayDay) : monthOf(pendingYear, pendingMonth)
  const heading =
    view === 'day' ? formats.monthYear.format(utcNoon(cursor)) : formats.monthYear.format(utcNoon(monthOf(pendingYear, pendingMonth)))
  // The grid's Tab stop: the day the keyboard left it on, else the picked day or today when this
  // month shows them, else the month's first day.
  const shownMonth = cursor.slice(0, 7)
  const tabDay = [focusDay, selected, todayDay].find((day) => day?.startsWith(shownMonth) === true) ?? cursor

  return (
    <div className={cn('relative flex flex-col gap-1', className)}>
      {hasLabel && <span className="block text-xs font-medium text-text-secondary">{label}</span>}
      {suffixed && (
        <span id={suffixId} className="sr-only">
          {labelSuffix}
        </span>
      )}
      {/* The field's frame holds the clear button beside the trigger, not on it (WCAG 2.5.8). */}
      <div
        className={cn(
          'flex min-h-10 w-full items-center rounded border bg-background has-[[aria-haspopup]:focus]:border-primary-main has-[[aria-haspopup]:focus]:ring-2 has-[[aria-haspopup]:focus]:ring-primary-main',
          error === undefined ? 'border-text-light' : 'border-error-main',
        )}
      >
        <button
          ref={triggerRef}
          type="button"
          onClick={openPicker}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={inRow ? undefined : accessibleName}
          aria-labelledby={labelledBy}
          data-invalid={error !== undefined || undefined}
          aria-describedby={descriptions === '' ? undefined : descriptions}
          className="flex min-h-10 min-w-0 flex-1 cursor-pointer items-center justify-between gap-2 self-stretch rounded px-3 py-1 text-left text-sm focus:outline-hidden"
        >
          {/* Wraps rather than cut the text short, as under a reader's wider text spacing (WCAG 1.4.12). */}
          <span id={shownId} className={cn('min-w-0 break-words', formatted === null ? 'text-text-light' : 'text-text-primary')}>
            {shown}
          </span>
          <Calendar size={16} className="shrink-0 text-text-light" />
        </button>
        {clearable && selected !== null && (
          <button
            type="button"
            aria-label={labels.clear}
            onClick={clear}
            className={cn(
              targetSize,
              'mr-2 shrink-0 cursor-pointer rounded text-text-light hover:bg-background-contrast-sm hover:text-text-secondary',
              focusRing,
            )}
          >
            <X size={14} />
          </button>
        )}
      </div>
      {error !== undefined && <FieldError id={errorId} message={error} />}

      {open && (
        <>
          {/* Pointer-only click-away; the keyboard closes it with Escape or by tabbing out. */}
          <div aria-hidden="true" onClick={close} className="fixed inset-0 z-40 cursor-default" />
          {/* Open in place rather than modal, so it sits by its field. */}
          <dialog
            open
            ref={dialogRef}
            aria-labelledby={titleId}
            tabIndex={-1}
            onKeyDown={holdScrollKeys}
            style={position}
            className="fixed right-auto z-50 m-0 overflow-y-auto rounded border border-text-primary bg-background p-0 text-text-primary shadow-card-hover focus:outline-hidden"
          >
            <div className="px-5 pb-4 pt-5">
              <p id={titleId} className="text-xs font-semibold uppercase tracking-wider text-text-light">
                {labels.title}
              </p>
              <p className="mt-1 font-heading text-2xl font-medium text-text-primary">
                {formats.headline.format(utcNoon(headline))}
              </p>
            </div>

            <div className="px-5 pb-5">
              <div className="mb-3 flex items-center justify-between">
                <button
                  id={headingId}
                  type="button"
                  onClick={drillUp}
                  className={cn(
                    'flex cursor-pointer items-center gap-1 rounded text-sm font-medium text-text-primary hover:text-primary-main',
                    focusRing,
                  )}
                >
                  {heading}
                  {view === 'year' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                <div className="flex items-center gap-1">
                  <button type="button" aria-label={labels.previous} onClick={previous} className={stepClass}>
                    <ChevronLeft size={16} />
                  </button>
                  <button type="button" aria-label={labels.next} onClick={next} className={stepClass}>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>

              {view === 'day' && (
                <DayGrid
                  labelledBy={headingId}
                  cursor={cursor}
                  tabDay={tabDay}
                  onMove={moveFocus}
                  selected={selected}
                  today={todayDay}
                  markedDays={markedDays}
                  markedLabel={labels.marked}
                  formats={formats}
                  onPick={pickDay}
                />
              )}

              {view === 'month' && (
                <>
                  <div className="grid grid-cols-3 gap-y-3 text-center text-sm">
                    {Array.from({ length: 12 }, (_, month) => (
                      <PickButton
                        key={month}
                        value={month}
                        pressed={month === pendingMonth}
                        label={formats.longMonth.format(utcNoon(monthOf(2026, month)))}
                        onPick={setPendingMonth}
                      >
                        {formats.shortMonth.format(utcNoon(monthOf(2026, month)))}
                      </PickButton>
                    ))}
                  </div>
                  <PickerFooter labels={labels} onCancel={cancelMonth} onOk={confirmMonth} />
                </>
              )}

              {view === 'year' && (
                <>
                  <div className="grid grid-cols-3 gap-y-3 text-center text-sm">
                    {Array.from({ length: 12 }, (_, index) => yearStart + index).map((year) => (
                      <PickButton
                        key={year}
                        value={year}
                        pressed={year === pendingYear}
                        label={String(year)}
                        onPick={setPendingYear}
                      >
                        {year}
                      </PickButton>
                    ))}
                  </div>
                  <PickerFooter labels={labels} onCancel={cancelYear} onOk={confirmYear} />
                </>
              )}
            </div>
          </dialog>
        </>
      )}
    </div>
  )
}

/** Keys a browser scrolls the page with while nothing that takes them has focus. */
const scrollKeys = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End'])

const stepClass = cn(
  'cursor-pointer rounded p-1 text-text-secondary hover:bg-background-contrast-sm',
  focusRing,
)

/** The first day of a month, with the month counted from 0 and allowed to run past either end. */
function monthOf(year: number, month: number): CalendarDay {
  return new Date(Date.UTC(year, month, 1, 12)).toISOString().slice(0, 10)
}

interface Formats {
  readonly fullDay: Intl.DateTimeFormat
  readonly weekday: Intl.DateTimeFormat
  readonly longWeekday: Intl.DateTimeFormat
}

/** The same day of the month `months` away, or that month's last day when it is shorter. */
function addMonths(day: CalendarDay, months: number): CalendarDay {
  const year = Number(day.slice(0, 4))
  const month = Number(day.slice(5, 7)) - 1 + months
  const last = Number(addDays(monthOf(year, month + 1), -1).slice(8))
  return `${monthOf(year, month).slice(0, 8)}${String(Math.min(Number(day.slice(8)), last)).padStart(2, '0')}`
}

/** Where a key moves the grid's focus from `day`, or `null` for a key the grid leaves alone. */
function dayAfterKey(day: CalendarDay, event: ReactKeyboardEvent): CalendarDay | null {
  const weekday = utcNoon(day).getUTCDay()
  switch (event.key) {
    case 'ArrowLeft':
      return addDays(day, -1)
    case 'ArrowRight':
      return addDays(day, 1)
    case 'ArrowUp':
      return addDays(day, -7)
    case 'ArrowDown':
      return addDays(day, 7)
    case 'Home':
      return addDays(day, -weekday)
    case 'End':
      return addDays(day, 6 - weekday)
    case 'PageUp':
      return addMonths(day, event.shiftKey ? -12 : -1)
    case 'PageDown':
      return addMonths(day, event.shiftKey ? 12 : 1)
    default:
      return null
  }
}

function DayGrid(props: {
  /** The id of the month and year heading that names the grid. */
  readonly labelledBy: string
  readonly cursor: CalendarDay
  /** The one day in the grid that Tab reaches. */
  readonly tabDay: CalendarDay
  readonly selected: CalendarDay | null
  readonly today: CalendarDay
  readonly markedDays: readonly CalendarDay[] | undefined
  readonly markedLabel: string | undefined
  readonly formats: Formats
  readonly onPick: (day: CalendarDay) => void
  /** Moves the grid's focus to another day, in another month if need be. */
  readonly onMove: (day: CalendarDay) => void
}) {
  const { cursor, formats, tabDay, onMove } = props
  const weekdays = useMemo(
    // Any week will do; 2026-01-04 is a Sunday.
    () => Array.from({ length: 7 }, (_, index) => addDays('2026-01-04', index)),
    [],
  )
  // The month's weeks, each seven cells long, blank before the 1st and after the last day.
  const weeks = useMemo(() => {
    const month = cursor.slice(0, 7)
    const blanks = utcNoon(cursor).getUTCDay()
    const all: (CalendarDay | null)[] = Array.from({ length: blanks }, () => null)
    for (let day = cursor; day.startsWith(month); day = addDays(day, 1)) {
      all.push(day)
    }
    while (all.length % 7 !== 0) {
      all.push(null)
    }
    return Array.from({ length: all.length / 7 }, (_, week) => all.slice(week * 7, week * 7 + 7))
  }, [cursor])

  const move = useCallback(
    (event: ReactKeyboardEvent<HTMLTableElement>) => {
      const next = dayAfterKey(tabDay, event)
      if (next !== null) {
        event.preventDefault()
        onMove(next)
      }
    },
    [tabDay, onMove],
  )

  return (
    // WAI-ARIA's date picker dialog pattern makes the month's table a grid, focusable only by
    // script; its days are the Tab stop.
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role -- a grid is a table the arrow keys move through
    <table role="grid" tabIndex={-1} aria-labelledby={props.labelledBy} onKeyDown={move} className="w-full table-fixed border-collapse">
      <thead>
        <tr className="text-center text-xs font-medium uppercase text-text-light">
          {weekdays.map((day) => (
            <th key={day} scope="col" className="py-1 font-medium">
              <span className="sr-only">{formats.longWeekday.format(utcNoon(day))}</span>
              <span aria-hidden="true">{formats.weekday.format(utcNoon(day))}</span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {weeks.map((week) => (
          <tr key={week.find((day) => day !== null)}>
            {week.map((day, index) =>
              day === null ? (
                // oxlint-disable-next-line react/no-array-index-key, jsx-a11y/control-has-associated-label -- a blank before the 1st or after the last has no other identity, and no day to name
                <td key={`blank-${String(index)}`} />
              ) : (
                <DayButton
                  key={day}
                  day={day}
                  isTabStop={day === tabDay}
                  selected={day === props.selected}
                  today={day === props.today}
                  marked={props.markedDays?.includes(day) === true}
                  label={
                    props.markedDays?.includes(day) === true && props.markedLabel !== undefined
                      ? `${formats.fullDay.format(utcNoon(day))}, ${props.markedLabel}`
                      : formats.fullDay.format(utcNoon(day))
                  }
                  onPick={props.onPick}
                />
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function DayButton(props: {
  readonly day: CalendarDay
  /** The grid's one Tab stop; arrow keys reach the other days. */
  readonly isTabStop: boolean
  readonly selected: boolean
  readonly today: boolean
  readonly marked: boolean
  readonly label: string
  readonly onPick: (day: CalendarDay) => void
}) {
  const { day, onPick } = props
  const pick = useCallback(() => onPick(day), [onPick, day])

  return (
    <td className="p-0 pt-1">
      <div className="flex flex-col items-center">
        <button
          type="button"
          onClick={pick}
          data-day={day}
          tabIndex={props.isTabStop ? 0 : -1}
          aria-pressed={props.selected}
          aria-current={props.today ? 'date' : undefined}
          aria-label={props.label}
          className={cn(
            'flex aspect-square w-full max-w-9 cursor-pointer items-center justify-center rounded-full text-sm transition',
            props.selected
              ? 'bg-primary-main text-primary-contrast'
              : props.today
                ? 'border border-primary-main text-primary-main'
                : 'text-text-primary hover:bg-background-contrast-sm',
            focusRing,
          )}
        >
          {Number(day.slice(8))}
        </button>
        {props.marked && <span className="-mt-1 h-1 w-1 rounded-full bg-secondary-main" />}
      </div>
    </td>
  )
}

function PickButton(props: {
  readonly value: number
  readonly pressed: boolean
  readonly label: string
  readonly onPick: (value: number) => void
  readonly children: ReactNode
}) {
  const { value, onPick } = props
  const pick = useCallback(() => onPick(value), [onPick, value])

  return (
    <button
      type="button"
      onClick={pick}
      aria-pressed={props.pressed}
      aria-label={props.label}
      className={cn(
        'mx-auto w-16 cursor-pointer rounded-full py-2 font-medium transition',
        props.pressed
          ? 'bg-primary-main text-primary-contrast'
          : 'text-text-primary hover:bg-background-contrast-sm',
        focusRing,
      )}
    >
      {props.children}
    </button>
  )
}

function PickerFooter(props: {
  readonly labels: DatePickerProps['labels']
  readonly onCancel: () => void
  readonly onOk: () => void
}) {
  return (
    <div className="mt-3 flex items-center justify-end gap-4 border-t border-divider pt-3">
      <button type="button" onClick={props.onCancel} className={footerClass}>
        {props.labels.cancel}
      </button>
      <button type="button" onClick={props.onOk} className={footerClass}>
        {props.labels.ok}
      </button>
    </div>
  )
}

const footerClass = cn(
  'cursor-pointer rounded text-sm font-medium text-primary-main hover:underline',
  focusRing,
)
